import { makeStyles } from '@mui/styles';
import { Checkbox, Paper, TextField, Typography } from 'apollo-react';
import Table, { compareStrings, createStringSearchFilter } from 'apollo-react/components/Table';
import React, { useEffect, useState } from 'react';

import TextFieldFilter from '../../../components/TableFilters/TextInputFilter';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import {
  ACTION_FOR_DUPLICATE_ITEM_ACCESSOR,
  ACTION_FOR_DUPLICATE_ITEM_HEADER,
  APPLICATION_COMPONENT_NAME_ACCESSOR,
  APPLICATION_COMPONENT_NAME_HEADER,
  COMMENT_ACCESSOR,
  COMMENT_HEADER,
  LAST_MODIFIED_BY_ACCESSOR,
  LAST_MODIFIED_BY_HEADER,
  LAST_MODIFIED_DATE_ACCESSOR,
  LAST_MODIFIED_DATE_HEADER,
  MESSAGE_ACCESSOR,
  MESSAGE_HEADER,
  STATUS_EXPORT_ACCESSOR,
  STATUS_EXPORT_HEADER,
  TYPE_ACCESSOR,
  TYPE_HEADER,
} from '../../constants/common.constants';
import { STATUSES } from '../../constants/export.constants';
import { modifyDate } from '../../utils/export.utils';
import InfoBlock from '../common/InfoBlock';
import ActionDropdownCell from '../controls/ActionDropdownCell';
import CommentTextFieldCell from '../controls/CommentTextFieldCell';
import StatusCell from '../controls/StatusCell';
import MessageCell from '../import/StatusTable/controls/MessageCell';

const useStyles = makeStyles({
  fieldContainer: {
    padding: 30,
  },
});

const columns = [
  {
    header: ACTION_FOR_DUPLICATE_ITEM_HEADER,
    accessor: ACTION_FOR_DUPLICATE_ITEM_ACCESSOR,
    width: 220,
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter(ACTION_FOR_DUPLICATE_ITEM_ACCESSOR),
    filterComponent: TextFieldFilter,
    customCell: ActionDropdownCell,
  },
  {
    header: APPLICATION_COMPONENT_NAME_HEADER,
    accessor: APPLICATION_COMPONENT_NAME_ACCESSOR,
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter(APPLICATION_COMPONENT_NAME_ACCESSOR),
    filterComponent: TextFieldFilter,
  },
  {
    header: TYPE_HEADER,
    accessor: TYPE_ACCESSOR,
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter(TYPE_ACCESSOR),
    filterComponent: TextFieldFilter,
  },
  {
    header: LAST_MODIFIED_DATE_HEADER,
    accessor: LAST_MODIFIED_DATE_ACCESSOR,
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter(LAST_MODIFIED_DATE_ACCESSOR),
    filterComponent: TextFieldFilter,
  },
  {
    header: LAST_MODIFIED_BY_HEADER,
    accessor: LAST_MODIFIED_BY_ACCESSOR,
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter(LAST_MODIFIED_BY_ACCESSOR),
    filterComponent: TextFieldFilter,
  },
  {
    header: STATUS_EXPORT_HEADER,
    accessor: STATUS_EXPORT_ACCESSOR,
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter(STATUS_EXPORT_ACCESSOR),
    filterComponent: TextFieldFilter,
    customCell: StatusCell,
  },
  {
    header: MESSAGE_HEADER,
    accessor: MESSAGE_ACCESSOR,
    width: 300,
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter(MESSAGE_ACCESSOR),
    filterComponent: TextFieldFilter,
    customCell: MessageCell,
  },
];

const editableCommentColumn = {
  header: COMMENT_HEADER,
  accessor: COMMENT_ACCESSOR,
  width: 250,
  sortFunction: compareStrings,
  filterFunction: createStringSearchFilter(COMMENT_ACCESSOR),
  filterComponent: TextFieldFilter,
  customCell: CommentTextFieldCell,
};

const commentColumn = {
  header: COMMENT_HEADER,
  accessor: COMMENT_ACCESSOR,
  width: 250,
  sortFunction: compareStrings,
  filterFunction: createStringSearchFilter(COMMENT_ACCESSOR),
  filterComponent: TextFieldFilter,
};

const CheckboxCell = ({ row: { id, selectedRows, handleSelectRow } }) => (
  <Checkbox checked={selectedRows.has(id)} onChange={() => handleSelectRow(id)} />
);

const ResultsTable = ({
  rows = [],
  selectedRows,
  setSelectedRows,
  setResultRows,
  isPreviewStep = false,
  buildFileComment = '',
  setBuildFileComment,
  buildFileName = '',
  setBuildFileName,
  setBuildFileUser,
  buildFileUser,
  isSavedBuildFile,
  savedBuildFileRows,
  isPackagesExport,
}) => {
  const translateString = useTranslation();
  const classes = useStyles();

  const [filteredRows, setFilteredRows] = useState(new Set());

  const selectedIntersection = new Set([...selectedRows].filter((x) => filteredRows.has(x)));
  const isAllSelected = selectedIntersection.size === filteredRows.size;
  const isIndeterminate =
    selectedIntersection.size > 0 && selectedIntersection.size < filteredRows.size;

  useEffect(() => {
    !filteredRows.size && setFilteredRows(new Set(rows.map((row) => row.id)));
  }, [filteredRows.size, rows, selectedRows]);

  const onCheckboxHeaderChange = () => {
    if (isAllSelected) {
      setSelectedRows(new Set(rows.filter((r) => !filteredRows.has(r.id)).map((row) => row.id)));
    } else {
      setSelectedRows(new Set(rows.filter((r) => filteredRows.has(r.id)).map((row) => row.id)));
    }
  };

  useEffect(() => {
    const rowsWithUpdatedStatus = rows.map((row) => {
      return {
        ...row,
        Status: savedBuildFileRows.has(row.id) ? STATUSES.CHECKED : STATUSES.PENDING,
      };
    });

    isSavedBuildFile && setResultRows(rowsWithUpdatedStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSavedBuildFile, savedBuildFileRows]);

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const onTableChange = (values) => {
    const { rows: filteredRows } = values;
    setFilteredRows(new Set(filteredRows.map((row) => row.id)));
  };

  const checkboxColumn = {
    header: (
      <Checkbox
        indeterminate={isIndeterminate}
        checked={isAllSelected}
        onChange={onCheckboxHeaderChange}
      />
    ),
    accessor: '',
    customCell: CheckboxCell,
  };

  const editRow = ({ rowId, key, value }) => {
    setResultRows((prev) =>
      prev.map((item) => {
        if (item.id !== rowId) {
          return item;
        }
        return {
          ...item,
          [key]: value,
        };
      })
    );
  };

  const columnsWithComments = !isPreviewStep
    ? [...columns, commentColumn]
    : [...columns, editableCommentColumn];

  const onChangePackageUser = ({ target }) => setBuildFileUser(target.value);
  const onChangePackageComment = ({ target }) => setBuildFileComment(target.value);
  const onChangePackageName = ({ target }) => setBuildFileName(target.value);

  const checkedInDate = `${modifyDate(new Date())}`;

  return (
    <Paper>
      <Table
        columns={isPreviewStep ? [checkboxColumn, ...columnsWithComments] : columnsWithComments}
        rows={rows.map((row) => ({
          ...row,
          handleSelectRow,
          selectedRows,
          setSelectedRows,
          editRow,
          disableActionDropdown: !isPreviewStep,
          isSavedBuildFile,
        }))}
        rowId="id"
        showFilterIcon
        onChange={onTableChange}
        defaultRowsPerPage={25}
        rowsPerPageOptions={[15, 25, 50, 200, translateString('All')]}
        emptyProps={{
          text: translateString('No data to display'),
        }}
        tablePaginationProps={{
          labelDisplayedRows: ({ from, to, count }) =>
            `${from}-${to} ${translateString('of')} ${count}`,
          truncate: false,
          labelRowsPerPage: translateString('Rows'),
          pageText: translateString('Page'),
          ofText: translateString('of'),
        }}
      />

      {isPreviewStep && (
        <div className={classes.fieldContainer}>
          {isSavedBuildFile && !isPackagesExport && (
            <InfoBlock>
              <Typography color="textSecondary">{`The package file was successfully checked in on ${checkedInDate}`}</Typography>
            </InfoBlock>
          )}

          <TextField
            name="buildFileName"
            value={buildFileName ?? ''}
            label="Package File Name"
            onChange={onChangePackageName}
            fullWidth
            InputProps={{
              inputProps: {
                maxLength: 50,
              },
            }}
          />

          {!isPackagesExport && (
            <TextField
              name="buildFileUser"
              value={buildFileUser ?? ''}
              label="Checked-In By"
              onChange={onChangePackageUser}
              fullWidth
              InputProps={{
                inputProps: {
                  maxLength: 50,
                },
              }}
            />
          )}

          <TextField
            name="buildFileComment"
            value={buildFileComment ?? ''}
            label="Package File Comment"
            onChange={onChangePackageComment}
            fullWidth
            multiline
            minRows={2}
            InputProps={{
              inputProps: {
                maxLength: 2000,
              },
            }}
          />
        </div>
      )}
    </Paper>
  );
};
export default ResultsTable;
